import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output, ViewChild } from "@angular/core";
import { IMeetingCustomData, IMeetingLocation, Meeting } from "@common/ADAPT.Common.Model/organisation/meeting";
import { MeetingAttendee } from "@common/ADAPT.Common.Model/organisation/meeting-attendee";
import { IMeetingDuration } from "@common/ADAPT.Common.Model/organisation/meeting-extensions";
import { CalendarIntegrationProvider } from "@common/ADAPT.Common.Model/organisation/organisation-detail";
import { Person } from "@common/ADAPT.Common.Model/person/person";
import { ImplementationKitService } from "@common/implementation-kit/implementation-kit.service";
import { ImplementationKitArticle } from "@common/implementation-kit/implementation-kit-article.enum";
import { Autobind } from "@common/lib/autobind.decorator/autobind.decorator";
import { IBreezeEntity } from "@common/lib/data/breeze-entity.interface";
import { CommonDataService } from "@common/lib/data/common-data.service";
import { EntityPersistentService } from "@common/lib/data/entity-persistent.service";
import { ArrayUtilities } from "@common/lib/utilities/array-utilities";
import { DateUtilities } from "@common/lib/utilities/date-utilities";
import { BaseComponent } from "@common/ux/base.component/base.component";
import { Event, ScheduleInformation } from "@microsoft/microsoft-graph-types";
import moment from "moment";
import { filter, lastValueFrom, of, skip, Subject, switchMap } from "rxjs";
import { catchError, debounceTime, tap } from "rxjs/operators";
import { ICalendarProvider, IMeetingOrganiserInfo } from "../../calendar/calendar.interface";
import { CalendarIntegrationUtilities } from "../../calendar/calendar-integration-utilities";
import { MicrosoftCalendarService } from "../../calendar/microsoft-calendar.service";
import { DirectoryAuthService } from "../../directory-shared/directory-auth.service";
import { DirectorySharedService } from "../../directory-shared/directory-shared.service";
import { OAuthService } from "../../oauth/oauth.service";
import { MeetingsService } from "../meetings.service";
import { MeetingsUiService } from "../meetings-ui.service";
import { SelectMeetingLocationComponent } from "../select-meeting-location/select-meeting-location.component";

interface IMeetingDurationWithTime extends IMeetingDuration {
    time: Date;
}

@Component({
    selector: "adapt-schedule-meeting",
    templateUrl: "./schedule-meeting.component.html",
    styleUrl: "./schedule-meeting.component.css",
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false,
})
export class ScheduleMeetingComponent extends BaseComponent implements OnInit {
    public readonly PrivateEmailLearnMoreUrl = ImplementationKitService.GetArticleLink(ImplementationKitArticle.CalendarIntegrationPrivateEmail);

    @Input() public meeting?: Meeting;
    @Input() public editableDetails = true;
    @Input() public showTitle = true;
    @Input() public disabled = false;
    @Input() public sourceMeetingDateTime?: Date;
    @Output() public entitiesChange = new EventEmitter<IBreezeEntity[]>();
    @Output() public meetingLocationChange = new EventEmitter<IMeetingLocation | undefined>();
    @Output() public hasConflictsChange = new EventEmitter<boolean>();
    @Output() public organiserInfoChange = new EventEmitter<IMeetingOrganiserInfo>();

    @Input() public sendInvitations = true;
    @Output() public sendInvitationsChange = new EventEmitter<boolean>();

    @Input() public createTeamsMeeting = false;
    @Output() public createTeamsMeetingChange = new EventEmitter<boolean>();


    @ViewChild(SelectMeetingLocationComponent) public selectMeetingLocationComponent!: SelectMeetingLocationComponent;

    public attendeePeople: Person[] = [];
    public deletedAttendees: MeetingAttendee[] = [];

    public meetingLocation?: IMeetingLocation;

    public showInvitationPrompt = false;
    public showTeamsPrompt = false;
    public checkingProviderStatus = true;
    private checkingProviderStatusUpdater = this.createThrottledUpdater<boolean>((loading) => this.checkingProviderStatus = loading, 250);

    public checkAvailability = new Subject<void>();
    public locationAvailable?: boolean;
    public peopleAvailable: string[] = [];
    public peopleUnavailable: string[] = [];
    public peopleNoEmail: string[] = [];
    public loadingAvailability = false;
    private loadingAvailabilityUpdater = this.createThrottledUpdater<boolean>((loading) => this.loadingAvailability = loading);

    public existingProviderMeeting?: Event;

    public meetingDurations?: IMeetingDurationWithTime[];
    public startTimeValidationError?: { message: string };
    public endTimeValidationError?: { message: string };

    public constructor(
        private microsoftCalendarService: MicrosoftCalendarService,
        private meetingsService: MeetingsService,
        private meetingsUiService: MeetingsUiService,
        private commonDataService: CommonDataService,
        private directorySharedService: DirectorySharedService,
        private directoryAuthService: DirectoryAuthService,
        private oauthService: OAuthService,
        private calendarIntegrationUtilities: CalendarIntegrationUtilities,
        private entityPersistentService: EntityPersistentService,
    ) {
        super();

        this.oauthService.authenticationStatusWithProvider$.pipe(
            skip(1), // skip first since we get this synchronously in ngOnInit
            this.takeUntilDestroyed(),
        ).subscribe(([authed, provider]) => this.onIntegrationAuthChange(authed, provider));

        this.checkAvailability.pipe(
            // we only support this for microsoft so far...
            switchMap(() => this.oauthService.isAuthedWithProvider(CalendarIntegrationProvider.Microsoft)),
            filter((isAuthed) => isAuthed),
            tap(() => this.loadingAvailabilityUpdater.next(true)),
            debounceTime(1000),
            switchMap(() => this.updateAvailability()),
            this.takeUntilDestroyed(),
        ).subscribe();
    }

    public get isOrganiser() {
        return this.meetingOrganiser?.isOrganiser ?? true;
    }

    public get organiserName() {
        return this.meetingOrganiser?.name ?? "the organiser";
    }

    private get meetingOrganiser() {
        if (this.existingProviderMeeting && this.meeting) {
            const customData = this.meeting.extensions.getCustomData<IMeetingCustomData>();
            return {
                isOrganiser: customData.microsoftUserId === this.oauthService.user?.userId,
                name: this.existingProviderMeeting.organizer?.emailAddress?.name ?? "the organiser",
            } as IMeetingOrganiserInfo;
        }

        return undefined;
    }

    public async ngOnInit() {
        if (this.meeting) {
            this.meetingDurations = this.getMeetingDurations();
            if (this.meeting.extensions.isNotStarted && this.meeting.entityAspect.entityState.isAdded()) {
                this.meetingsService.updateMeetingDurationFromAgendaItems(this.meeting);
            }

            await lastValueFrom(this.meetingsService.initialiseMeeting(this.meeting));

            this.attendeePeople = this.meeting.meetingAttendees.map((attendee) => attendee.attendee);

            // restore the meeting location from the saved customData
            const customData = this.meeting.extensions.getCustomData<IMeetingCustomData>();
            if (this.meeting.location || customData.microsoftLocation) {
                this.meetingLocation = {
                    name: this.meeting.location,
                    emailAddress: customData.microsoftLocation,
                };

                // make sure we fire this so that it's included in runData for calendar integration
                this.onMeetingLocationChange(this.meetingLocation);
            }

            await this.updateIntegration();

            this.emitEntityChange();
            this.validateEndTime();

            // if this is provided, this is scheduling the next instance of this meeting
            if (this.sourceMeetingDateTime) {
                this.startTimeValidationError = {
                    message: "This is an automatically suggested date for the meeting. Please make sure this is correct.",
                };
            }
        }
    }

    private async updateIntegration() {
        // clear the remembered provider meeting so we can check it again
        this.existingProviderMeeting = undefined;
        const [authed, provider] = await lastValueFrom(this.oauthService.authenticationStatusWithProvider);
        await this.onIntegrationAuthChange(authed, provider);
    }

    public onSendInvitationsChange(sendInvitations: boolean, updateCustomData = true) {
        this.sendInvitations = sendInvitations;
        this.sendInvitationsChange.emit(sendInvitations);

        // save invitation sent status into custom data so we can restore that state while editing
        if (updateCustomData) {
            const customData = this.meeting!.extensions.getCustomData<IMeetingCustomData>();
            customData.invitationsSent = sendInvitations;
            this.meeting!.extensions.updateCustomData(customData);
            this.emitEntityChange();
        }

        if (!this.sendInvitations) {
            this.onCreateTeamsMeetingChange(false);
        }
    }

    public onCreateTeamsMeetingChange(createTeamsMeeting: boolean) {
        const originalCreateTeamsMeeting = this.createTeamsMeeting;
        this.createTeamsMeeting = createTeamsMeeting;
        this.createTeamsMeetingChange.emit(createTeamsMeeting);

        // update a meeting field to cause changes, so that entityTypeChanged will fire on save
        if (originalCreateTeamsMeeting !== this.createTeamsMeeting) {
            this.meeting!.lastUpdatedDateTime = new Date();
        }
    }

    public attachExistingRemoteMeeting() {
        this.meetingsUiService.linkExistingCalendarIntegrationMeeting(this.meeting!, false).pipe(
            switchMap(() => this.updateIntegration()),
        ).subscribe();
    }

    public detachExistingRemoteMeeting() {
        this.oauthService.authenticationStatusWithProvider.pipe(
            filter(([_, provider]) => !!provider),
            switchMap(([_, provider]) => this.meetingsUiService.detachCalendarIntegrationMeeting(this.meeting!, provider!, false)),
            switchMap(() => this.updateIntegration()),
        ).subscribe();
    }

    @Autobind
    public meetingAttendeeFilterFunction(person: Person) {
        if (!this.meeting) {
            return false;
        }

        return !this.meeting.meetingAttendees.find((meetingAttendee) => meetingAttendee.attendeeId === person.personId)
            && this.meetingsService.personCanParticipateInMeeting(person, this.meeting)
            && (!this.meeting.team?.isPrivate || this.meetingsService.personCanEditMeeting(person, this.meeting));
    }

    public async onMeetingAttendeesChange(people: Person[]) {
        if (this.meeting) {
            const removedPeople = this.attendeePeople.filter((p) => !people.includes(p));
            for (const person of removedPeople) {
                const existingAttendee = this.meeting.meetingAttendees.find((a) => a.attendee === person);
                if (existingAttendee) {
                    // don't need to record the deleted attendee if it was added without saving
                    if (!existingAttendee.entityAspect.entityState.isAdded()) {
                        this.deletedAttendees.push(existingAttendee);
                    }
                    await lastValueFrom(this.commonDataService.remove(existingAttendee));
                    ArrayUtilities.removeElementFromArray(person, this.attendeePeople);
                }
            }

            const addedPeople = people.filter((p) => !this.attendeePeople.includes(p));
            for (const person of addedPeople) {
                // this attendee was already deleted, just revert those changes
                const deletedAttendee = this.deletedAttendees.find((attendee) => attendee.attendeeId === person.personId);
                if (deletedAttendee) {
                    deletedAttendee.entityAspect.rejectChanges();
                    ArrayUtilities.removeElementFromArray(deletedAttendee, this.deletedAttendees);
                } else {
                    // person has not been an attendee, add them
                    const attendee = await lastValueFrom(this.meetingsService.createMeetingAttendee(this.meeting!.meetingId, person.personId));

                    // manually persist the attendee, otherwise will only be persisted if other meeting property changes
                    this.entityPersistentService.persistEntity(attendee);
                }

                ArrayUtilities.addElementIfNotAlreadyExists(this.attendeePeople, person);
            }

            // only emit the entity change if anything actually changed
            if ((removedPeople.length + addedPeople.length) > 0) {
                this.emitEntityChange();
            }

            if (people.length) {
                // prime all contacts for the attendees so we can get their email
                // filter by profiles you can read for.
                await this.directorySharedService.promiseToGetContactDetailsByPersonIds(people
                    .filter((person) => this.directoryAuthService.currentPersonCanReadPersonsProfile(person))
                    .map((person) => person.personId));
            }

            this.checkAvailability.next();
        }
    }

    public onMeetingLocationChange(value?: IMeetingLocation) {
        const previousLocation = this.meetingLocation;

        this.meetingLocation = value;
        this.meetingLocationChange.emit(value);

        this.meeting!.location = this.meetingLocation?.name;

        const customData = this.meeting!.extensions.getCustomData<IMeetingCustomData>();
        customData.microsoftLocation = this.meetingLocation?.id;
        this.meeting!.extensions.updateCustomData(customData);

        this.emitEntityChange();

        if (!this.meetingLocation) {
            // location unset, therefore hide location available message
            this.locationAvailable = undefined;
            return;
        }

        this.locationAvailable = this.selectMeetingLocationComponent?.isLocationAvailable(this.meetingLocation);

        // meetingLocation has an email (i.e. does have availability), and we don't have the availability already
        // therefore check availability
        // also force a check if they select the same location twice (say for example they deleted a meeting or a schedule changed)
        const locationHasChanged = previousLocation !== this.meetingLocation;
        if (this.meetingLocation.emailAddress && (this.locationAvailable === undefined || !locationHasChanged)) {
            this.checkAvailability.next();
        }
    }

    public onMeetingStartTimeChange(date: Date) {
        this.meeting!.extensions.setMeetingDate(date);

        // update the durations so that the End Time displayed is correct
        this.meetingDurations = this.getMeetingDurations();
        this.meeting!.extensions.updateSelectedDuration();

        // clear the suggested date warning
        this.startTimeValidationError = undefined;

        this.emitEntityChange();
        this.checkAvailability.next();
    }

    private getMeetingDurations() {
        return this.meeting!.extensions.durationOptions.map((o: IMeetingDurationWithTime) => {
            o.time = moment(this.meeting!.meetingDateTime).add(o.minuteOffset, "minutes").toDate();
            return o;
        });
    }

    public onMeetingDurationChange(endDuration: IMeetingDuration) {
        this.meeting!.extensions.setMeetingEndDuration(endDuration);
        this.emitEntityChange();
        this.checkAvailability.next();
        this.validateEndTime();
    }

    private validateEndTime() {
        const totalAgendaTime = MeetingsService.getMeetingAgendaItemsTotalPlannedDurationInMinutes(this.meeting?.meetingAgendaItems ?? []);
        const expectedEndTime = moment(this.meeting!.meetingDateTime)
            .add({ minutes: totalAgendaTime });
        this.endTimeValidationError = expectedEndTime.isAfter(this.meeting!.endTime)
            ? { message: `There won't be enough time to run this meeting. The agenda will require ${DateUtilities.minuteToDurationString(totalAgendaTime, false)}.` }
            : undefined;
    }

    private emitEntityChange() {
        this.entitiesChange.emit([
            this.meeting!,
            this.meeting!.supplementaryData!,
            ...this.meeting!.meetingAttendees,
            ...this.deletedAttendees,
        ]);
    }

    private async onIntegrationAuthChange(authed: boolean, provider?: ICalendarProvider) {
        if (!this.meeting) {
            return;
        }

        const isNewMeeting = this.meeting.entityAspect.entityState.isAdded();
        this.checkingProviderStatusUpdater.next(!isNewMeeting);

        const isAuthedWithProvider = authed && !!provider;
        this.showTeamsPrompt = isAuthedWithProvider && provider?.id === CalendarIntegrationProvider.Microsoft;
        this.showInvitationPrompt = isAuthedWithProvider
            && [CalendarIntegrationProvider.Local, CalendarIntegrationProvider.Microsoft].includes(provider.id);

        const providerMeetingId = this.calendarIntegrationUtilities.getProviderMeetingId(this.meeting, CalendarIntegrationProvider.Microsoft);

        // check if there is a teams meeting already for this meeting.
        // new meetings won't have an existing teams meeting.
        // if we are importing a meeting from Microsoft, it could have one already
        if (this.showTeamsPrompt && !this.existingProviderMeeting && (!isNewMeeting || providerMeetingId)) {
            this.existingProviderMeeting = await lastValueFrom(this.calendarIntegrationUtilities.getProviderMeeting(this.meeting));
            const shouldCreateTeamsMeeting = this.createTeamsMeeting || (this.existingProviderMeeting?.isOnlineMeeting ?? false);
            this.onCreateTeamsMeetingChange(shouldCreateTeamsMeeting);
        }

        const organiser = this.meetingOrganiser;
        this.organiserInfoChange.emit(organiser);

        // don't want to sent invites for ended meetings or if we explicitly didn't send invitations
        // or if the user isn't the original organiser
        const customData = this.meeting.extensions.getCustomData<IMeetingCustomData>();
        const sendInvitations = !this.meeting.extensions.isEnded
            && (customData.invitationsSent ?? true)
            && (organiser?.isOrganiser ?? true);
        this.onSendInvitationsChange(sendInvitations, false);

        if (!authed) {
            // reset all availability when logging out
            this.locationAvailable = undefined;
            this.peopleAvailable = [];
            this.peopleUnavailable = [];
            this.peopleNoEmail = [];
        }

        if (isAuthedWithProvider) {
            this.checkAvailability.next();
        }

        this.checkingProviderStatusUpdater.next(false);
    }

    private updateAvailability() {
        return this.selectMeetingLocationComponent.getMeetingLocations().pipe(
            switchMap((locations) => {
                const meetingLocations = this.meetingLocation?.emailAddress ? locations.concat(this.meetingLocation) : locations;
                return this.microsoftCalendarService.getCalendarAvailability(this.meeting!, meetingLocations);
            }),
            catchError(() => of([] as ScheduleInformation[])),
            tap((availability) => {
                this.loadingAvailabilityUpdater.next(false);

                // passes availability to select-meeting-location, so we don't have to query it twice
                this.selectMeetingLocationComponent.setLocationAvailability(availability, this.meeting!);

                // select-meeting-location already knows if the room is available or not, so pull that info from there
                this.locationAvailable = this.meetingLocation
                    ? this.selectMeetingLocationComponent.isLocationAvailable(this.meetingLocation)
                    : undefined;

                this.peopleAvailable = [];
                this.peopleUnavailable = [];
                this.peopleNoEmail = this.attendeePeople
                    .filter((p) => !p.getLoginEmail()?.value)
                    .map((p) => p.firstName);
                const personFromEmail: Record<string, Person> = Object.fromEntries(this.attendeePeople.map((p) => [p.getLoginEmail()?.value, p]));
                for (const schedule of availability) {
                    const person = schedule.scheduleId
                        ? personFromEmail[schedule.scheduleId]
                        : undefined;
                    if (person) {
                        const available = this.microsoftCalendarService.isScheduleAvailable(schedule, this.meeting!);
                        (available ? this.peopleAvailable : this.peopleUnavailable).push(person.firstName);
                    }
                }

                this.hasConflictsChange.emit(this.locationAvailable === false || this.peopleUnavailable.length > 0);
            }),
        );
    }
}
